/**
 * Text-level styling inside a table cell, like the Rich Text toolbar: STYLING (template builder,
 * Properties) applies to the text selected in the cell's editor when there is a selection, and to the
 * whole cell (`table.cellStyles`) otherwise.
 *
 * Properties' colour and size inputs take focus, which clears the browser selection, so the last
 * non-empty selection made in a cell editor is kept here and restored when a style is applied. It is
 * dropped on a click anywhere outside Properties and on a collapsed selection inside an editor.
 *
 * Styled text is stored in the cell's html as `<span data-cell-style style="…">` — html cells already
 * render as html in the note, the preview and the PDF.
 */

const CELL_EDITOR_SELECTOR = '.table-mention-wrapper [contenteditable="true"]';
const PROPERTIES_SELECTOR = '.properties-wrapper';
const STYLED_SPAN_ATTR = 'data-cell-style';
// Whole-cell only: a background behind part of the text is not offered.
const TEXT_STYLE_KEYS = ['color', 'fontWeight', 'fontStyle', 'textDecoration', 'fontSize'];
const TOGGLES = {
    fontWeight: { on: 'bold', off: 'normal', isOn: (cs) => Number(cs.fontWeight) >= 600 || cs.fontWeight === 'bold' },
    fontStyle: { on: 'italic', off: 'normal', isOn: (cs) => cs.fontStyle === 'italic' },
    textDecoration: { on: 'underline', off: 'none', isOn: (cs) => (cs.textDecorationLine || cs.textDecoration || '').includes('underline') },
};

/**
 * Whether `el`'s text shows the toggle on. Weight and italic are inherited, so the element's own computed
 * style says it; an underline is not inherited (an ancestor draws it), so the element and its ancestors up to
 * the cell are checked.
 */
const showsToggle = (key, el) => {
    if (key !== 'textDecoration') return TOGGLES[key].isOn(getComputedStyle(el));
    for (let node = el; node && node.nodeType === Node.ELEMENT_NODE; node = node.parentElement) {
        if (TOGGLES.textDecoration.isOn(getComputedStyle(node))) return true;
        if (node.matches('td, th')) break;
    }
    return false;
};

let saved = null; // { editor, range }
let tracking = false;
const listeners = new Set();

// The kept selection stays highlighted while Properties' size / colour inputs have focus (the browser
// hides a selection that lost focus). Drawn by the CSS Custom Highlight API, styled in formStyles.scss
// (`::highlight(table-cell-text-selection)`); no DOM changes. Browsers without it show no highlight there.
const HIGHLIGHT_NAME = 'table-cell-text-selection';
const paintHighlight = () => {
    if (typeof CSS === 'undefined' || !CSS.highlights || !window.Highlight) return;
    if (saved) CSS.highlights.set(HIGHLIGHT_NAME, new window.Highlight(saved.range));
    else CSS.highlights.delete(HIGHLIGHT_NAME);
};

const setSaved = (next) => {
    saved = next;
    paintHighlight();
    listeners.forEach((fn) => fn());
};

const editorOf = (node) => (node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement)?.closest?.(CELL_EDITOR_SELECTOR) || null;

const onSelectionChange = () => {
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;
    const range = selection.getRangeAt(0);
    const editor = editorOf(range.commonAncestorContainer);
    if (!editor) return; // focus moved to Properties (or elsewhere): keep the last selection
    setSaved(range.collapsed ? null : { editor, range: range.cloneRange() });
};

const onMouseDown = (e) => {
    if (!e.target?.closest?.(PROPERTIES_SELECTOR) && saved) setSaved(null);
};

/** Call `fn` whenever the kept selection changes or is restyled; returns the unsubscribe. */
export const subscribeCellTextSelection = (fn) => {
    listeners.add(fn);
    return () => listeners.delete(fn);
};

/**
 * The kept selection's look, in STYLING's shape (`cellColor`, `cellBold`, `cellItalic`, `cellUnderline`,
 * `cellSize` without `px`), read from the first selected character — or null when nothing is selected.
 */
export const getCellTextSelectionStyles = () => {
    if (!saved?.editor.isConnected) return null;
    const { startContainer, startOffset } = saved.range;
    const node =
        startContainer.nodeType === Node.TEXT_NODE ? startContainer.parentElement : startContainer.childNodes[startOffset] || startContainer;
    const el = node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;
    if (!el) return null;
    const cs = getComputedStyle(el);
    return {
        cellColor: rgbToHex(cs.color),
        cellBold: showsToggle('fontWeight', el),
        cellItalic: showsToggle('fontStyle', el),
        cellUnderline: showsToggle('textDecoration', el),
        cellSize: (cs.fontSize || '').replace('px', ''),
    };
};

/** `rgb(23, 43, 77)` → `#172b4d`, what the colour input takes; anything else as it is. */
const rgbToHex = (color) => {
    const m = /^rgba?\((\d+),\s*(\d+),\s*(\d+)/.exec(color || '');
    return m ? `#${m.slice(1, 4).map((n) => Number(n).toString(16).padStart(2, '0')).join('')}` : color || '';
};

/** Start keeping the last cell-text selection (once per page). */
export const trackCellTextSelection = () => {
    if (tracking) return;
    tracking = true;
    document.addEventListener('selectionchange', onSelectionChange);
    document.addEventListener('mousedown', onMouseDown, true);
};

export const clearCellTextSelection = () => {
    if (saved) setSaved(null);
};

// Tags that set a text style by themselves, without a `style` attribute.
const STYLE_TAGS = { fontWeight: 'b, strong', fontStyle: 'i, em', textDecoration: 'u' };

/**
 * A cell's html with `styleKey` taken off everything inside it — inline styles (pasted text carries its own
 * font sizes) and the tags that imply it (`<b>`, `<i>`, `<u>`) — so a whole-cell style reaches all of the
 * cell's text. Styled spans left with no style are unwrapped. Other html is returned as it is.
 */
export const clearCellHtmlStyle = (html, styleKey) => {
    if (typeof html !== 'string' || !html.includes('<') || !TEXT_STYLE_KEYS.includes(styleKey)) return html;
    const root = document.createElement('div');
    root.innerHTML = html;
    root.querySelectorAll('[style]').forEach((el) => {
        el.style[styleKey] = '';
        if (styleKey === 'textDecoration') el.style.textDecorationLine = '';
        if (!el.getAttribute('style')) el.removeAttribute('style');
    });
    const unwrap = (el) => el.replaceWith(...el.childNodes);
    if (STYLE_TAGS[styleKey]) root.querySelectorAll(STYLE_TAGS[styleKey]).forEach(unwrap);
    root.querySelectorAll(`span[${STYLED_SPAN_ATTR}]:not([style])`).forEach(unwrap);
    return root.innerHTML === html ? html : root.innerHTML;
};

/** The text nodes the range covers, split at its ends so each one lies wholly inside it. */
const textNodesIn = (range) => {
    const nodes = [];
    const root = range.commonAncestorContainer;
    if (root.nodeType === Node.TEXT_NODE) {
        nodes.push(root);
    } else {
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
        while (walker.nextNode()) if (range.intersectsNode(walker.currentNode)) nodes.push(walker.currentNode);
    }
    return nodes
        .map((node) => {
            let text = node;
            if (text === range.endContainer && range.endOffset < text.length) text.splitText(range.endOffset);
            if (text === range.startContainer && range.startOffset > 0) text = text.splitText(range.startOffset);
            return text;
        })
        // Token chips are not restyled piecemeal; a style around them still applies to their text.
        .filter((text) => text.textContent.length && !text.parentElement?.closest('.mention'));
};

/** The node's own styled span when it is that span's only content, else a new span around it. */
const styledSpanFor = (text) => {
    const parent = text.parentElement;
    if (parent?.hasAttribute(STYLED_SPAN_ATTR) && parent.childNodes.length === 1) return parent;
    const span = document.createElement('span');
    span.setAttribute(STYLED_SPAN_ATTR, '');
    text.parentNode.insertBefore(span, text);
    span.appendChild(text);
    return span;
};

/**
 * Apply `styleKey` to the kept selection. Bold / italic / underline toggle on what is selected (all
 * on → off), whatever `value` says; colour and size take `value` ('' removes it). Returns false when
 * there is no selection to style, so the caller styles the whole cell instead.
 */
export const applyCellTextStyle = (styleKey, value) => {
    if (!saved || !TEXT_STYLE_KEYS.includes(styleKey) || !saved.editor.isConnected) return false;
    const { editor, range } = saved;
    const texts = textNodesIn(range);
    if (!texts.length) return false;

    const toggle = TOGGLES[styleKey];
    const next = toggle ? (texts.every((t) => showsToggle(styleKey, t.parentElement)) ? toggle.off : toggle.on) : value;
    const spans = texts.map(styledSpanFor);
    spans.forEach((span) => {
        span.style[styleKey] = next || '';
        if (!span.getAttribute('style')) span.removeAttribute('style');
    });

    // Keep the same text selected for the next change (typing 3 → 32 in the size box restyles, not nests).
    const kept = document.createRange();
    kept.setStartBefore(spans[0]);
    kept.setEndAfter(spans[spans.length - 1]);
    // The editor reports its html on input (`MentionableInput.handleInput`).
    editor.dispatchEvent(new Event('input', { bubbles: true }));
    setSaved({ editor, range: kept });
    return true;
};
